import { useCallback, useSyncExternalStore } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'ck_theme'

/**
 * Theme state lives in a module-level store, not in each hook instance.
 *
 * `useTheme` used to be plain `useState`. It is mounted up to THREE times at
 * once — the desktop nav toggle, the mobile-overlay toggle, and the admin
 * sidebar toggle — and each copy held its own `theme`. Toggling one flipped
 * `data-theme` correctly (so the page recoloured) but left every other copy
 * showing the wrong icon and the wrong `aria-label`, which for a screen-reader
 * user is worse than the wrong icon: the control lies about what it will do.
 *
 * `useSyncExternalStore` over one store fixes that by construction — the same
 * primitive `Navigation.tsx` already uses for scroll position.
 *
 * NOTE: the pre-hydration IIFE in index.html duplicates `readStoredTheme()`.
 * That duplication is deliberate and load-bearing — it has to run before any
 * module is parsed, or the first paint is the wrong theme. `theme.test.ts`
 * pins the two implementations together.
 */

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // localStorage unavailable (private mode, blocked cookies) — fall through
  }
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

let current: Theme = readStoredTheme()
const listeners = new Set<() => void>()

// Mirror the resolved value onto the document immediately. index.html already
// does this before React loads, so in the browser this is a no-op reassert --
// but it keeps the store authoritative for tests and for any entry point that
// does not go through index.html.
//
// Deliberately does NOT write localStorage: persisting on mere page load would
// turn an OS-derived value into an explicit choice and permanently stop the
// prefers-color-scheme listener below from following the system.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', current)
}

function emit() {
  for (const l of listeners) l()
}

function apply(theme: Theme) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme)
  }
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // ignore persistence failure
  }
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

const getSnapshot = () => current
// The server and the pre-hydration script both resolve their own value; the
// hook only needs a stable one here to avoid a hydration mismatch warning.
const getServerSnapshot = (): Theme => 'light'

export function setTheme(theme: Theme): void {
  if (theme === current) return
  current = theme
  apply(theme)
  emit()
}

export function toggleTheme(): void {
  setTheme(current === 'light' ? 'dark' : 'light')
}

/**
 * Follow the OS preference until the visitor expresses one of their own.
 *
 * Previously `prefers-color-scheme` was read once at init and never again, so
 * flipping the OS theme with the page open did nothing. Once someone has used
 * the toggle, their choice is in localStorage and wins — changing your OS theme
 * should not silently undo a deliberate choice.
 */
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const onSystemChange = (e: MediaQueryListEvent) => {
    let hasExplicitChoice = false
    try {
      hasExplicitChoice = localStorage.getItem(STORAGE_KEY) !== null
    } catch {
      // treat an unreadable store as "no explicit choice"
    }
    if (hasExplicitChoice) return
    current = e.matches ? 'dark' : 'light'
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', current)
    }
    emit()
  }
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onSystemChange)
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const toggle = useCallback(() => toggleTheme(), [])
  const set = useCallback((t: Theme) => setTheme(t), [])
  return { theme, toggle, setTheme: set }
}

/**
 * Test-only: re-read storage and reset the module store.
 *
 * Needed because the store is initialised ONCE at import, which is the point of
 * the fix -- three mounted toggles share one value. A test that seeds
 * localStorage after import has to ask the store to re-read.
 */
export function __resetThemeForTests(theme?: Theme): void {
  listeners.clear()
  current = theme ?? readStoredTheme()
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', current)
  }
}
